import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cartService from './cartService';
import toast from 'react-hot-toast'; // Ensure you import toast if used

// Initial State
const initialState = {
    cart: { items: [] },
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: '',
};

// Async Thunks

// Fetch Cart
export const fetchCart = createAsyncThunk('cart/fetch', async (token, thunkAPI) => {
    try {
        const response = await cartService.getCart(token);
        return response; // Assuming the API returns the full cart object
    } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to fetch cart';
        toast.error(message);
        return thunkAPI.rejectWithValue(message);
    }
});

// Add Item to Cart
export const addToCart = createAsyncThunk(
    'cart/addToCart',
    async (cartData, thunkAPI) => {
        try {
            const response = await cartService.addToCart(cartData);
            toast.success('Item added to cart');
            return response; // Assuming the API returns the updated cart
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to add item to cart';
            toast.error(message);
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Remove Item from Cart
export const removeFromCart = createAsyncThunk(
    'cart/remove',
    async ({ cartItemId, businessId, token }: { cartItemId: string, businessId: string, token: string }, thunkAPI) => {
        try {
            const response = await cartService.removeFromCart(cartItemId, businessId, token);
            return response; // Return the full response if needed
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || error.message || 'Failed to remove item from cart'
            );
        }
    }
);

// Cart Slice
const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        resetCart: (state) => {
            state.isLoading = false;
            state.isError = false;
            state.isSuccess = false;
            state.message = '';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Cart
            .addCase(fetchCart.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCart.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cart = action.payload.data; // Update with fetched cart data
            })
            .addCase(fetchCart.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            // Add to Cart
            .addCase(addToCart.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(addToCart.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cart = action.payload.data; // Update with the new cart state
            })
            .addCase(addToCart.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            // Remove from Cart
            .addCase(removeFromCart.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(removeFromCart.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cart = action.payload.cart; // Update with the new cart state
            })
            .addCase(removeFromCart.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            });
    },
});

// Actions
export const { resetCart } = cartSlice.actions;

// Reducer
export default cartSlice.reducer;
